import { supabase } from './supabase';

export interface StageMetrics {
  stageId: string;
  stageName: string;
  count: number;
  totalValue: number;
  averageValue: number;
  averageDaysInStage: number;
  conversionRate: number;
}

export interface ConversionMetrics {
  fromStageId: string;
  toStageId: string;
  fromStageName: string;
  toStageName: string;
  count: number;
  conversionRate: number;
}

export interface WinLossMetrics {
  totalOpportunities: number;
  wonCount: number;
  lostCount: number;
  activeCount: number;
  winRate: number;
  lossRate: number;
  totalWonValue: number;
  totalLostValue: number;
  averageWonValue: number;
  lossReasons: Array<{ reason: string; count: number }>;
}

export interface ActivityMetrics {
  totalActivities: number;
  callCount: number;
  emailCount: number;
  meetingCount: number;
  noteCount: number;
  averageActivitiesPerOpportunity: number;
  activitiesByType: Array<{ type: string; count: number }>;
}

export interface RevenueForecast {
  period: string;
  expectedRevenue: number;
  weightedRevenue: number;
  opportunityCount: number;
}

export const analyticsService = {
  async getStageMetrics(brandId: string): Promise<StageMetrics[]> {
    const { data: stages, error: stagesError } = await supabase
      .from('pipeline_stages')
      .select('id, name, order_index')
      .eq('brand_id', brandId)
      .order('order_index');

    if (stagesError || !stages) {
      console.error('Error loading stages:', stagesError);
      return [];
    }

    const { data: opportunities, error: oppsError } = await supabase
      .from('opportunities')
      .select('pipeline_stage_id, value, created_at, updated_at')
      .eq('brand_id', brandId)
      .is('won_date', null)
      .is('lost_date', null);

    if (oppsError || !opportunities) {
      console.error('Error loading opportunities:', oppsError);
      return [];
    }

    const { data: activities } = await supabase
      .from('opportunity_activities')
      .select('opportunity_id, activity_type, created_at')
      .eq('activity_type', 'stage_change')
      .order('created_at', { ascending: false });

    const stageChangeMap = new Map<string, Date>();
    activities?.forEach((activity) => {
      if (!stageChangeMap.has(activity.opportunity_id)) {
        stageChangeMap.set(activity.opportunity_id, new Date(activity.created_at));
      }
    });

    const metrics: StageMetrics[] = stages.map((stage) => {
      const stageOpps = opportunities.filter((opp) => opp.pipeline_stage_id === stage.id);
      const count = stageOpps.length;
      const totalValue = stageOpps.reduce((sum, opp) => sum + Number(opp.value), 0);
      const averageValue = count > 0 ? totalValue / count : 0;

      const daysInStage = stageOpps.map((opp) => {
        const enteredDate = stageChangeMap.get(opp.pipeline_stage_id) || new Date(opp.updated_at);
        const now = new Date();
        return (now.getTime() - enteredDate.getTime()) / (1000 * 60 * 60 * 24);
      });

      const averageDaysInStage =
        daysInStage.length > 0
          ? daysInStage.reduce((sum, days) => sum + days, 0) / daysInStage.length
          : 0;

      return {
        stageId: stage.id,
        stageName: stage.name,
        count,
        totalValue,
        averageValue,
        averageDaysInStage,
        conversionRate: 0,
      };
    });

    for (let i = 0; i < metrics.length - 1; i++) {
      const currentStage = metrics[i];
      const nextStage = metrics[i + 1];
      if (currentStage.count > 0) {
        currentStage.conversionRate = (nextStage.count / currentStage.count) * 100;
      }
    }

    return metrics;
  },

  async getWinLossMetrics(brandId: string): Promise<WinLossMetrics> {
    const { data: opportunities, error } = await supabase
      .from('opportunities')
      .select('value, won_date, lost_date, lost_reason')
      .eq('brand_id', brandId);

    if (error || !opportunities) {
      console.error('Error loading win/loss data:', error);
      return {
        totalOpportunities: 0,
        wonCount: 0,
        lostCount: 0,
        activeCount: 0,
        winRate: 0,
        lossRate: 0,
        totalWonValue: 0,
        totalLostValue: 0,
        averageWonValue: 0,
        lossReasons: [],
      };
    }

    const wonOpps = opportunities.filter((opp) => opp.won_date);
    const lostOpps = opportunities.filter((opp) => opp.lost_date);
    const activeOpps = opportunities.filter((opp) => !opp.won_date && !opp.lost_date);

    const totalWonValue = wonOpps.reduce((sum, opp) => sum + Number(opp.value), 0);
    const totalLostValue = lostOpps.reduce((sum, opp) => sum + Number(opp.value), 0);

    const lossReasonMap = new Map<string, number>();
    lostOpps.forEach((opp) => {
      const reason = opp.lost_reason || 'Unknown';
      lossReasonMap.set(reason, (lossReasonMap.get(reason) || 0) + 1);
    });

    const lossReasons = Array.from(lossReasonMap.entries())
      .map(([reason, count]) => ({ reason, count }))
      .sort((a, b) => b.count - a.count);

    const closedCount = wonOpps.length + lostOpps.length;

    return {
      totalOpportunities: opportunities.length,
      wonCount: wonOpps.length,
      lostCount: lostOpps.length,
      activeCount: activeOpps.length,
      winRate: closedCount > 0 ? (wonOpps.length / closedCount) * 100 : 0,
      lossRate: closedCount > 0 ? (lostOpps.length / closedCount) * 100 : 0,
      totalWonValue,
      totalLostValue,
      averageWonValue: wonOpps.length > 0 ? totalWonValue / wonOpps.length : 0,
      lossReasons,
    };
  },

  async getActivityMetrics(brandId: string): Promise<ActivityMetrics> {
    const { data: opportunities } = await supabase
      .from('opportunities')
      .select('id')
      .eq('brand_id', brandId);

    if (!opportunities) return {
      totalActivities: 0,
      callCount: 0,
      emailCount: 0,
      meetingCount: 0,
      noteCount: 0,
      averageActivitiesPerOpportunity: 0,
      activitiesByType: [],
    };

    const opportunityIds = opportunities.map((opp) => opp.id);

    const { data: activities, error } = await supabase
      .from('opportunity_activities')
      .select('activity_type')
      .in('opportunity_id', opportunityIds);

    if (error || !activities) {
      console.error('Error loading activity metrics:', error);
      return {
        totalActivities: 0,
        callCount: 0,
        emailCount: 0,
        meetingCount: 0,
        noteCount: 0,
        averageActivitiesPerOpportunity: 0,
        activitiesByType: [],
      };
    }

    const typeMap = new Map<string, number>();
    activities.forEach((activity) => {
      typeMap.set(activity.activity_type, (typeMap.get(activity.activity_type) || 0) + 1);
    });

    const activitiesByType = Array.from(typeMap.entries())
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);

    return {
      totalActivities: activities.length,
      callCount: typeMap.get('call') || 0,
      emailCount: typeMap.get('email') || 0,
      meetingCount: typeMap.get('meeting') || 0,
      noteCount: typeMap.get('note') || 0,
      averageActivitiesPerOpportunity: opportunities.length > 0 ? activities.length / opportunities.length : 0,
      activitiesByType,
    };
  },

  async getRevenueForecast(brandId: string): Promise<RevenueForecast[]> {
    const { data: opportunities, error } = await supabase
      .from('opportunities')
      .select('value, probability, expected_close_date')
      .eq('brand_id', brandId)
      .is('won_date', null)
      .is('lost_date', null)
      .not('expected_close_date', 'is', null);

    if (error || !opportunities) {
      console.error('Error loading forecast data:', error);
      return [];
    }

    const forecastMap = new Map<string, { expected: number; weighted: number; count: number }>();

    opportunities.forEach((opp) => {
      const date = new Date(opp.expected_close_date!);
      const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const value = Number(opp.value);
      const probability = opp.probability || 50;

      const existing = forecastMap.get(period) || { expected: 0, weighted: 0, count: 0 };
      forecastMap.set(period, {
        expected: existing.expected + value,
        weighted: existing.weighted + (value * probability) / 100,
        count: existing.count + 1,
      });
    });

    return Array.from(forecastMap.entries())
      .map(([period, data]) => ({
        period,
        expectedRevenue: data.expected,
        weightedRevenue: data.weighted,
        opportunityCount: data.count,
      }))
      .sort((a, b) => a.period.localeCompare(b.period));
  },
};
